import type { EventDto, InboxFilter } from '@fos/shared/contracts/inbox';
import type { EventSource, Severity } from '@fos/shared/enums';
import { EVENT_SOURCE_LABELS } from '@fos/shared/labels';
import { addDaysLocal, diffDaysLocal, localToUtc, toLocalDate } from '@fos/shared/time';
import { formatDateShort, formatRelativePast, formatTime } from '../../lib/format';

/** Dni tygodnia w liście Inboxu (Figma 02: „sob.”, „pt.”, „czw.”). */
const WEEKDAYS = ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.'] as const;

function weekdayOf(localDate: string): number {
  return new Date(`${localDate}T12:00:00Z`).getUTCDay();
}

/**
 * Czas w wierszu listy (Figma 02): dziś — godzina („9:46”), wczoraj — „wczoraj”, w ostatnim tygodniu — dzień
 * tygodnia („sob.”), wcześniej — data („4.10”).
 */
export function listTime(at: Date, now: Date = new Date()): string {
  const day = toLocalDate(at);
  const today = toLocalDate(now);
  const diff = diffDaysLocal(day, today);
  if (diff <= 0) return formatTime(at);
  if (diff === 1) return 'wczoraj';
  if (diff < 7) return WEEKDAYS[weekdayOf(day)] ?? formatDateShort(at, now);
  return formatDateShort(at, now);
}

/** Czas w podglądzie: „wczoraj, 18:42”, „12 min temu”, „3.10 · 10:00”. */
export function detailTime(at: Date, now: Date = new Date()): string {
  return formatRelativePast(at, now);
}

/** Nadawca w liście: nadawca zdarzenia („Anna · REA”, „Monitoring”) albo źródło z klientem („Automat · REA”). */
export function senderLine(event: Pick<EventDto, 'sender' | 'source' | 'client'>): string {
  if (event.sender) return event.sender;
  const source = EVENT_SOURCE_LABELS[event.source as EventSource] ?? 'System';
  return event.client ? `${source} · ${event.client.name}` : source;
}

export interface SnoozePreset {
  key: string;
  label: string;
  until: Date;
}

/** Szybkie terminy „Odłóż” (Europe/Warsaw): za 3 godziny, jutro 8:00, w poniedziałek 8:00, za tydzień. */
export function snoozePresets(now: Date = new Date()): SnoozePreset[] {
  const today = toLocalDate(now);
  const weekday = weekdayOf(today);
  const toMonday = (8 - weekday) % 7 || 7;
  return [
    { key: 'later', label: 'Za 3 godziny', until: new Date(now.getTime() + 3 * 3_600_000) },
    { key: 'tomorrow', label: 'Jutro, 8:00', until: localToUtc(addDaysLocal(today, 1), '08:00') },
    { key: 'monday', label: 'W poniedziałek, 8:00', until: localToUtc(addDaysLocal(today, toMonday), '08:00') },
    { key: 'week', label: 'Za tydzień, 8:00', until: localToUtc(addDaysLocal(today, 7), '08:00') },
  ];
}

/** „do jutra, 8:00”, „do pon. 12.10, 8:00”, „do 9:30”. */
export function snoozedUntilLabel(until: Date, now: Date = new Date()): string {
  const day = toLocalDate(until);
  const diff = diffDaysLocal(toLocalDate(now), day);
  if (diff <= 0) return `do ${formatTime(until)}`;
  if (diff === 1) return `do jutra, ${formatTime(until)}`;
  return `do ${WEEKDAYS[weekdayOf(day)]} ${formatDateShort(until, now)}, ${formatTime(until)}`;
}

/** Puste stany listy (spokojnie, jedno zdanie). */
export const EMPTY_LIST_TEXT: Record<InboxFilter, { title: string; description: string }> = {
  open: {
    title: 'Inbox jest pusty',
    description: 'Wiadomości i zdarzenia, które wymagają Twojej uwagi, pojawią się tutaj.',
  },
  unread: { title: 'Wszystko przeczytane', description: 'Nowe wiadomości pojawią się tutaj.' },
  snoozed: { title: 'Nic nie jest odłożone', description: 'Odłożone wiadomości wrócą do Inboxu w wybranym terminie.' },
  done: { title: 'Nic tu jeszcze nie ma', description: 'Wiadomości oznaczone jako zrobione trafią tutaj.' },
  all: { title: 'Inbox jest pusty', description: 'Wiadomości i zdarzenia pojawią się tutaj.' },
};

/** Odebrany mail „Do wiadomości” = „Mniej ważne” (17.10.2026): jest w Inboxie, ale wizualnie ciszej. */
export function isLessImportantMail(e: Pick<EventDto, 'type' | 'severity'>): boolean {
  return e.type === 'message.received' && e.severity === 'fyi';
}

/** Ważności w filtrze listy (z „Do wiadomości” = historia). */
export const FILTER_SEVERITIES: Severity[] = ['critical', 'approval', 'normal', 'fyi'];
